import { Link } from '@tanstack/react-router'
import { useAuth } from '../contexts/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { useClubhouseBook } from '../hooks/useClubhouseBook'
import { useStrokesGainedOverview } from '../hooks/useStrokesGained'
import type { FeedRound, RegisterEntry } from '../hooks/useClubhouseBook'
import { formatIndex, formatPlayedOn, formatStamp, formatToPar } from '../lib/golfFormat'
import {
  BookGate,
  BrassRule,
  ClubLinkButton,
  ClubPage,
  Container,
  EmptyPlate,
  Eyebrow,
  MemberCard,
  Scorecard,
  Zone,
} from '../components/golf'
import type { ScorecardColumn, ScorecardRow } from '../components/golf'

/**
 * The Clubhouse (route map §4 `/clubhouse`) — the member's front room, and
 * where auth#verify lands after a magic link is claimed.
 *
 * Publicly browsable, like the rest of the clubhouse: a visitor sees the whole
 * room, with the personal parts standing in as invitations rather than data.
 *
 * The book and the register are REAL now. The recent-rounds table is
 * `GET /golf/rounds/feed` — posted rounds only, from anyone — and the handicap
 * beside the member's card is that member's line in `GET /golf/register`. Both
 * endpoints are membership-gated while this page is not (see
 * Fireside::MembershipGate), so a standing that does not open them renders the
 * club's own gate here rather than bouncing a member off the page.
 *
 * The table's leading column is the ROUND, and it is a link — once per round,
 * on its first player's row, so a four-ball is one link rather than four to the
 * same page. To-par is `round_par` (the par of the holes actually played) and
 * is a dash when the server sends none: a to-par derived from anything else
 * would be a number the club made up. The "You" chip is `is_you`, which the
 * server computes from the reader's id and NEVER a display-name match — two
 * members are allowed to share a name, and only one of them is you.
 */

/** Local-clock greeting — the one thing on this page that is genuinely live. */
function greetingFor(hour: number): { salutation: string; part: string } {
  if (hour < 12) return { salutation: 'Good morning', part: 'morning' }
  if (hour < 18) return { salutation: 'Good afternoon', part: 'afternoon' }
  return { salutation: 'Good evening', part: 'evening' }
}

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

const ROUND_COLUMNS: ScorecardColumn[] = [
  { key: 'round', label: 'Round' },
  { key: 'member', label: 'Member' },
  { key: 'course', label: 'Course' },
  { key: 'holes', label: 'Holes', numeric: true },
  { key: 'gross', label: 'Gross', numeric: true },
  { key: 'toPar', label: 'To par', numeric: true },
]

/** The roster chip — "Guest" and "You" share one geometry on purpose. */
function RosterChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-2 whitespace-nowrap rounded-club-sm border border-ink-soft/35 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-ink-soft">
      {children}
    </span>
  )
}

/**
 * One table line per player of each posted round, newest round first.
 *
 * ONE LINK PER ROUND, on its first player's row. A four-ball is one round and
 * one page; four links to it in a column is four ways to press the same thing,
 * and a screen reader would read the same destination four times.
 */
function roundRows(rounds: FeedRound[]): ScorecardRow[] {
  return rounds.flatMap((round) =>
    round.players.map((player, index) => ({
      id: `${round.id}-${index}`,
      // The server's own answer to "is this row the reader", never a name
      // match: two members may share a display name and only one is you.
      isYou: player.is_you,
      cells: {
        round:
          index === 0 ? (
            <Link
              to={`/clubhouse/rounds/${round.id}`}
              className="rounded-club-sm font-semibold text-fairway"
            >
              {formatPlayedOn(round.played_on)}
            </Link>
          ) : (
            ''
          ),
        member: (
          <>
            {player.display_name}
            {player.guest && <RosterChip>Guest</RosterChip>}
            {player.is_you && <RosterChip>You</RosterChip>}
          </>
        ),
        course: round.course_name,
        holes: round.holes_count,
        // An unfinished card can reach the feed with no total on a player who
        // never had a stroke entered. Say so rather than print a 0.
        gross: player.total_strokes ?? '—',
        // Both halves or nothing. `round_par` is null when any played hole has
        // no par, and a gross is null when nobody entered a stroke; either way
        // the club has nothing to compare and says so.
        toPar: formatToPar(
          player.total_strokes === null || round.round_par === null
            ? null
            : player.total_strokes - round.round_par,
        ),
      },
    })),
  )
}

export function ClubhouseScreen() {
  usePageTitle('The Clubhouse')
  const { isAuthenticated, user } = useAuth()
  const { rounds, register, isLoading, error, membershipRequired } =
    useClubhouseBook(isAuthenticated)
  const strokesGained = useStrokesGainedOverview(isAuthenticated)

  const now = new Date()
  const { salutation, part } = greetingFor(now.getHours())
  const name = user?.display_name?.trim() || user?.email?.split('@')[0]

  // The member's own line in the register, matched on id — the register is the
  // one place the club states a member's index, so the page never computes one.
  const standing: RegisterEntry | undefined = register.find((entry) => entry.user_id === user?.id)
  const indexKeptOn = standing ? formatStamp(standing.handicap_updated_at) : null
  const rows = roundRows(rounds)

  return (
    <ClubPage chrome="clubhouse">
      {/* ── The greeting ── */}
      <section className="relative overflow-hidden py-15">
        <svg
          viewBox="0 0 1200 420"
          preserveAspectRatio="none"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 size-full opacity-40"
        >
          <path d="M-50 350 Q300 336 620 358 T1250 344" stroke="var(--color-sage)" fill="none" strokeWidth="1.2" />
          <path d="M-50 382 Q300 368 620 390 T1250 376" stroke="var(--color-brass)" fill="none" strokeWidth="1" opacity=".5" />
          <path d="M-50 410 Q320 396 640 418 T1250 404" stroke="var(--color-fairway)" fill="none" strokeWidth="1.2" opacity=".6" />
        </svg>

        <Container className="grid items-center gap-14 lg:grid-cols-[1fr_380px]">
          <div>
            <Eyebrow>
              The Clubhouse · {WEEKDAYS[now.getDay()]} {part}
            </Eyebrow>
            <h1 className="mb-4.5 mt-4 font-display text-[clamp(2.375rem,4.6vw,3.5rem)] font-medium leading-[1.04] tracking-[-0.014em] text-pine">
              {salutation}
              {name ? `, ${name}` : ''}.
            </h1>
            <p className="max-w-[46ch] font-display text-[17.5px] italic leading-[1.5] text-ink-soft">
              <b className="font-semibold not-italic text-fairway">The steward&rsquo;s note —</b> fair
              skies over all eighteen, the register is open, and there is time yet for a round that
              fits the day.
            </p>
          </div>

          <div className="lg:ml-auto lg:w-[380px]">
            <MemberCard name={name ?? 'Your name here'} tier={isAuthenticated ? 'Member' : 'Unclaimed'} />

            {/* Only ever drawn from the member's own line in the register. No
                standing in the book ⇒ no panel, rather than a hopeful dash. */}
            {standing && (
              <div className="mt-5 rounded-club-lg border border-ink/12 bg-parchment px-6 py-5">
                <dl className="flex items-baseline justify-between gap-4">
                  <dt className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep">
                    Handicap index
                  </dt>
                  <dd className="font-display text-[27px] tabular-nums leading-none text-pine">
                    {formatIndex(standing.handicap_index)}
                  </dd>
                </dl>
                <p className="mt-2.5 text-[11.5px] text-ink-soft">
                  {standing.handicap_index === null
                    ? 'Three counting rounds set an index. The club will not guess at one before then.'
                    : `Kept as of ${indexKeptOn ?? 'your last posted round'}.`}
                </p>
              </div>
            )}
          </div>
        </Container>
      </section>

      {/* ── The rooms ── */}
      <section>
        <Container>
          {/* Four rooms, so a grid that balances four: one row at desktop
              width, two-up on a tablet, a column on a phone. The card's
              three-column row (clubhouse-home.html:86) orphaned the fourth
              tile alone on a second row. */}
          <div className="grid gap-5.5 md:grid-cols-2 lg:grid-cols-4">
            <Zone
              index="I. The Grounds"
              title="Play a round"
              description="Eighteen holes, always in season, never overbooked. Walk out whenever the day allows."
              to="/clubhouse/grounds"
            />
            <Zone
              index="II. The Register"
              title="Your standing"
              description="Handicap, last round, and the season's tally — kept from your first tee shot onward."
              to="/clubhouse/register"
            />
            <Zone
              index="III. The Lounge"
              title="What's on"
              description="Where the club convenes between rounds. Members only, as ever."
              to="/clubhouse/lounge"
            />
            <Zone
              index="IV. The Outings"
              title="Get a game together"
              description="A tee time nearby, your four invited, and the booking marked done. The club keeps the thread."
              to="/clubhouse/outings"
            />
          </div>
        </Container>
      </section>

      {isAuthenticated && (
        // The teaser as the clubhouse-home card draws it: a pine band, the
        // eyebrow, the line, the figure beside its label, one link. The card
        // paints the eyebrow brass; §3 keeps brass off text, so it is sage here
        // and on the card alike.
        <section aria-labelledby="your-game-teaser-heading" className="on-pine mt-16 bg-pine py-8 text-paper">
          <Container className="grid items-center gap-8 md:grid-cols-[minmax(0,1fr)_auto]">
            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-sage">Private · only you</p>
              <h2 id="your-game-teaser-heading" className="mt-1 font-display text-[28px] font-medium text-paper">Your game, stroke by stroke.</h2>
              <p
                role="status"
                aria-label="Your Game status"
                aria-live="polite"
                aria-atomic="true"
                className="mt-2 max-w-[58ch] text-[13px] text-sage"
              >
                {strokesGained.isLoading
                  ? 'Reading your ledger…'
                  : strokesGained.error
                    ? 'Strokes gained could not be read just now.'
                    : strokesGained.overview?.benchmark.version === 'unavailable'
                      ? 'Your observations are saved. The 2025 ShotLink prototype benchmark is not installed, so no value is estimated.'
                      : strokesGained.overview
                        ? `${strokesGained.overview.trend.complete_rounds} complete · ${strokesGained.overview.trend.holes_analyzed} holes analyzed`
                        : ''}
              </p>
            </div>
            <div className="grid min-w-[220px] grid-cols-[auto_auto] items-baseline gap-x-6 gap-y-2">
              <b className="font-display text-[38px] font-semibold tabular-nums">
                {strokesGained.overview && strokesGained.overview.benchmark.version !== 'unavailable' && strokesGained.overview.trend.total ? (
                  strokesGained.overview.trend.total.display
                ) : (
                  <>
                    <span aria-hidden="true">—</span>
                    <span className="sr-only">Unavailable</span>
                  </>
                )}
              </b>
              <span className="text-[10px] uppercase tracking-[0.12em] text-sage">
                {strokesGained.overview && strokesGained.overview.benchmark.version !== 'unavailable' ? (
                  <>
                    {'Last five '}
                    <br />
                    SG / 18
                  </>
                ) : (
                  <>
                    {'Benchmark '}
                    <br />
                    unavailable
                  </>
                )}
              </span>
              <Link to="/clubhouse/your-game" className="col-span-full rounded-club-sm text-[12px] font-bold text-paper underline underline-offset-4">
                Open Your Game <span aria-hidden="true">→</span>
              </Link>
            </div>
          </Container>
        </section>
      )}

      {/* ── Recent rounds ── */}
      <section className="pt-16">
        <Container>
          <div className="mb-5.5 flex items-baseline gap-4.5">
            <h2 className="font-display text-2xl font-medium text-pine">Recent rounds</h2>
            <BrassRule className="flex-1" />
            <Link
              to="/clubhouse/register"
              className="whitespace-nowrap rounded-club-sm text-[13.5px] font-semibold text-fairway"
            >
              The full register →
            </Link>
          </div>

          {/* The gate stands on the data rather than the door — visitor,
              standing, fault — and what follows is only ever the book itself. */}
          <BookGate
            isAuthenticated={isAuthenticated}
            membershipRequired={membershipRequired}
            error={error}
          >
            {isLoading && <p className="text-[13.5px] text-ink-soft">Opening the book…</p>}
            {!isLoading && rows.length === 0 && (
              <EmptyPlate
                title="No rounds entered yet."
                description="The book opens with your first tee shot. Nine holes or eighteen — the course will not check its watch."
                action={
                  <ClubLinkButton to="/clubhouse/grounds" variant="ghost" size="sm">
                    Walk out to the grounds
                  </ClubLinkButton>
                }
              />
            )}
            {rows.length > 0 && (
              <div className="overflow-x-auto rounded-club-lg border border-ink/12 bg-parchment px-6.5 pb-1.5 pt-6.5">
                <Scorecard
                  caption="Rounds posted to the clubhouse, newest first"
                  columns={ROUND_COLUMNS}
                  rows={rows}
                />
              </div>
            )}
          </BookGate>
        </Container>
      </section>
    </ClubPage>
  )
}
